<template>
  <aside class="Cart-Receipt">
    <CartSavings :subtotal="subtotal" :original-subtotal="originalSubtotal" />
    <form id="cart-receipt" action="/cart" method="post" novalidate>
      <input type="hidden" name="discount" :value="swdCodes" />
      <div class="Cart-Receipt__Lines">
        <div class="Cart-Receipt__Line">
          <p class="p4">{{ store.terms.subtotal }}</p>
          <p class="p4">
            <Price
              :cart="props?.items"
              :location="'subtotal'"
              :ready="store.promoReady"
            />
          </p>
          <p class="Cart-Receipt__DiscountLabel t5" data-color="green">
            <span>{{ store.terms.discounts_applied }}</span>
            <Icon :icon="'check-background'" />
          </p>
        </div>
        <div class="Cart-Receipt__Line">
          <p class="p4 Cart-Receipt__ShippingLine">
            {{ store.terms.shipping.label }}
            <FreeShippingTooltip
              v-if="showFreeShippingTooltip"
              :text="store.terms.free_shipping.copy"
            />
          </p>
          <p class="p4">{{ shipping }}</p>
          <ul v-if="shippingText" class="p5">
            <li v-if="shippingContext.text_line_1" data-color="gray-text">
              {{ shippingContext.text_line_1 }}
            </li>
            <li v-if="shippingContext.text_line_2" data-color="gray-text">
              {{ shippingContext.text_line_2 }}
            </li>
          </ul>
        </div>
        <div class="Cart-Receipt__Line">
          <p class="p4">{{ store.terms.taxes }}</p>
          <p class="p4">{{ store.terms.calculated_checkout }}</p>
        </div>
      </div>
      <GiftNote v-if="!store.cartOnlyContainsEgiftCard" />
      <Loyalty />
      <StickyUrgency class="StickyUrgency--Inline" />
      <button
        id="cart-receipt-submit"
        type="submit"
        role="button"
        name="checkout"
        class="Cart-Receipt__Submit Cart-Receipt__Submit--Inline checkout-now"
      >
        {{ store.terms.submit }}
      </button>
    </form>
  </aside>
</template>

<script setup>
import Icon from '../Global/Icon.vue';
import FreeShippingTooltip from '../Global/FreeShippingTooltip.vue';
import CartSavings from './CartSavings.vue';
import GiftNote from './GiftNote.vue';
import StickyUrgency from './StickyUrgency.vue';
import Loyalty from '../Global/Loyalty.vue';
import Price from '../Global/Price.vue';
import { computed, watch } from 'vue';
import { useCartStore } from './Composables/Store';
import { getSubtotal } from '@utils/price-helpers';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showFreeShippingTooltip: {
    type: [String, Boolean],
    default: false,
  },
});

const store = useCartStore();

// If the cart becomes e-gift-card-only while a physical gift note is already
// attached (e.g. the last physical product was removed), strip the note so a
// hidden note product isn't left in the cart and charged.
watch(
  () => store.cartOnlyContainsEgiftCard,
  (onlyEgiftCard) => {
    if (onlyEgiftCard && store.hasNote) store.removeGiftNote();
  },
  { immediate: true }
);

const showFreeShippingTooltip = computed(
  () => JSON.parse(props.showFreeShippingTooltip) ?? false
);

const originalSubtotal = computed(() => getSubtotal(props?.items, true));
const subtotal = computed(() =>
  getSubtotal(props?.items, false, {
    swd: window.Brooklinen?.promo?.discounts?.swd?.enabled,
    tiered: window.Brooklinen?.promo?.discounts?.tiers?.enabled,
  })
);
const shipping = computed(() => store.getShipping);

const shippingText = computed(() => {
  const shippingData = window.Brooklinen.shipping?.[0];
  const shippingEnabled = shippingData?.enabled == 'true';
  return shippingEnabled ? shippingData : false;
});

const shippingContext = computed(() => {
  if (shippingText.value) return shippingText.value;
  return store.terms.shipping;
});

const swdCodes = computed(() => {
  if (store?.tiers?.enabled) {
    const tierCodes = `${store.tiers.tier1.code},${store.tiers.tier2.code},${store.tiers.tier3.code}`;
    return tierCodes;
  } else {
    return store.swd?.code;
  }
});
</script>
